// components/header.js (only the dynamic bits changed)
import React, { useState, useEffect, useMemo } from "react";
import { NavLink, useLocation } from "react-router-dom";
import I19Logo from "../media/I19vapen.png";
import { api } from "../lib/api";

export default function Header({ path = "", sectionText = "I19 IF", data, logoSrc }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const isActive = (prefix) =>
    prefix === "/" ? location.pathname === "/" : location.pathname.startsWith(prefix);

  // --- find sections ---
  const sections = data?.sections || [];
  const newsSection = useMemo(
    () => sections.find(s => (s.slug || "").toLowerCase() === "nyheter" || (s.type || "").toLowerCase() === "news"),
    [sections]
  );
  const pagesSection = useMemo(
    () => sections.find(s => (s.slug || "").toLowerCase() === "sidor" || (s.type || "").toLowerCase() === "pages"),
    [sections]
  );
  const linksSection = useMemo(
    () => sections.find(s =>
      ["länkar","lankar","links"].includes((s.slug || "").toLowerCase()) ||
      (s.type || "").toLowerCase() === "links"
    ),
    [sections]
  );

  // --- fetch items ---
  const [pageItems, setPageItems] = useState([]);
  useEffect(() => {
    let active = true;
    (async () => {
      if (!pagesSection?._id) return void (active && setPageItems([]));
      try {
        const items = await api.itemsBySection(pagesSection._id);
        if (active) setPageItems(Array.isArray(items) ? items : []);
      } catch { if (active) setPageItems([]); }
    })();
    return () => { active = false; };
  }, [pagesSection?._id]);

  const [linkItems, setLinkItems] = useState([]);
  useEffect(() => {
    let active = true;
    (async () => {
      if (!linksSection?._id) return void (active && setLinkItems([]));
      try {
        const list = await api.itemsBySection(linksSection._id); // _id, name, link only
        // pull full item (with `input`) for each _id
        const full = await Promise.all(
          (list || []).map(async it => {
            try { return await api.itemById(it._id); } catch { return null; }
          })
        );
        // keep only those with a non-empty `input`
        const valid = full.filter(Boolean).filter(it => (it.input && String(it.input).trim()));
        if (active) setLinkItems(valid);
      } catch { if (active) setLinkItems([]); }
    })();
    return () => { active = false; };
  }, [linksSection?._id]);

  // --- helpers & merged menu ---
  const normalize = (s="") => s.toLowerCase().normalize("NFD").replace(/\p{Diacritic}/gu, "");
  const ensureHref = (s="") => {
    const t = String(s).trim();
    if (!t) return null;
    // allow http(s), protocol-relative, mailto, tel
    if (/^(https?:|mailto:|tel:)/i.test(t) || /^\/\//.test(t)) return t;
    // add https if missing scheme
    return `https://${t}`;
  };

  const homeHref = `${path || "/"}`.replace(/\/$/, "") || "/";
  const nyheterHref = `${path}/nyheter`.replace(/\/\//g, "/");

  // pages -> internal /sidor/<slug> (skip 'hem')
  const pageLinks = (pageItems || [])
    .filter(it => it?.link && normalize(it.link) !== "hem")
    .map(it => ({
      key: it._id || `p:${it.link}`,
      label: it.name || it.link,
      type: "internal",
      to: `${path}/sidor/${it.link}`.replace(/\/{2,}/g, "/"),
    }));

  // länkar -> EXTERNAL to item.input (only items with input were kept above)
  const externalLinks = (linkItems || [])
    .map(it => ({ ...it, href: ensureHref(it.input) }))
    .filter(it => !!it.href)
    .map(it => ({
      key: it._id || `e:${it.href}`,
      label: it.name || it.href,
      type: "external",
      href: it.href,
    }));

  const dynamicLinks = [...pageLinks, ...externalLinks];

  // close drawer + scroll to top on route change
  useEffect(() => {
    setOpen(false);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);

  return (
    <header>
      <div className="flex justify-between items-center p-8 bg-primary shadow-md shadow-neutral-600/75 md:border-b-2 md:border-neutral-200c sm:shadow-neutral-400/25">
        <div className="flex gap-3 items-center">
          <img className="w-1/6 md:w-[10%] lg:w-1/12" src={logoSrc || I19Logo} alt="i19if.jpg" loading="lazy" decoding="async" />
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-neutral-200">{sectionText}</h1>
        </div>

        {/* cross-site (desktop) */}
        <nav className="hidden flex-col justify-center items-center gap-5 md:flex-row md:mt-0 md:shadow-none md:flex">
          <NavLink className={`text-neutral-200 ${isActive("/") ? "underline font-semibold" : ""}`} to="/">Föreningen</NavLink>
          <NavLink className={`text-neutral-200 ${isActive("/boxning") ? "underline font-semibold" : ""}`} to="/boxning">Boxning</NavLink>
          <NavLink className={`text-neutral-200 ${isActive("/ol-ol-skytte") ? "underline font-semibold" : ""}`} to="/ol-ol-skytte">OL/OL-Skytte</NavLink>
          <NavLink className={`text-neutral-200 ${isActive("/skidor") ? "underline font-semibold" : ""}`} to="/skidor">Skidor</NavLink>
          <NavLink className={`text-neutral-200 ${isActive("/skidskytte") ? "underline font-semibold" : ""}`} to="/skidskytte">Skidskytte</NavLink>
        </nav>

        <button className="space-y-2 md:hidden" aria-label="Öppna meny" onClick={() => setOpen(true)}>
          <div className="w-8 h-0.5 bg-neutral-200"></div>
          <div className="w-8 h-0.5 bg-neutral-200"></div>
          <div className="w-8 h-0.5 bg-neutral-200"></div>
        </button>
      </div>

      {/* slide-in (mobile) */}
      <div className={[
        "flex gap-4 flex-col fixed right-0 top-0 bg-primary shadow-md shadow-neutral-600/75 h-screen w-[50vw] items-center p-5 transition duration-500 ease-in-out",
        open ? "translate-x-0" : "translate-x-full",
        "md:block md:p-0 md:bg-transparent md:h-fit md:static md:w-full md:transform-none",
      ].join(" ")}>
        <button className="text-neutral-200 ml-auto mr-6 mt-4 text-3xl md:hidden" aria-label="Stäng meny" onClick={() => setOpen(false)}>×</button>

        {/* cross-site (mobile) */}
        <nav className="flex flex-col justify-center items-center gap-5 bg-primary py-4 border-b border-neutral-200 md:flex-row md:mt-0 md:shadow-none md:hidden">
          <NavLink className="text-neutral-200" to="/" onClick={() => setOpen(false)}>Föreningen</NavLink>
          <NavLink className="text-neutral-200" to="/boxning" onClick={() => setOpen(false)}>Boxning</NavLink>
          <NavLink className="text-neutral-200" to="/ol-ol-skytte" onClick={() => setOpen(false)}>OL/OL-Skytte</NavLink>
          <NavLink className="text-neutral-200" to="/skidor" onClick={() => setOpen(false)}>Skidor</NavLink>
          <NavLink className="text-neutral-200" to="/skidskytte" onClick={() => setOpen(false)}>Skidskytte</NavLink>
        </nav>

        {/* in-site row: Hem, Nyheter, then ALL items from Sidor + Länkar */}
        <nav className="flex flex-col text-neutral-200 justify-center items-center gap-5 p-4 md:flex-row md:text-neutral-900">
          <NavLink to={homeHref} end className={({ isActive }) => (isActive ? "underline font-semibold" : "")}>
            Hem
          </NavLink>
          {newsSection && (
            <NavLink to={nyheterHref} className={({ isActive }) => (isActive ? "underline font-semibold" : "")}>
              Nyheter
            </NavLink>
          )}
          {dynamicLinks.map(item =>
            item.type === "external" ? (
              <a
                key={item.key}
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                className="underline-offset-4 hover:underline"
                onClick={() => setOpen(false)}
              >
                {item.label}
              </a>
            ) : (
              <NavLink
                key={item.key}
                to={item.to}
                className={({ isActive }) => (isActive ? "underline font-semibold" : "")}
              >
                {item.label}
              </NavLink>
            )
          )}
        </nav>
      </div>
    </header>
  );
}
